import React from 'react';
import {
    NSLabel, NSInputNumberStepper, NSSwitch,
} from 'bricks';
import {
    FORM_ROW_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds } from 'constants/proForma';
import { IProFormaValues } from 'views/ProFormaTable/types';
import { numeralFormatter } from 'ns_libs/formatter';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import StoryAreaBreakdown from '../StoryAreaBreakdown/StoryAreaBreakdown';

export interface IProFormaSizeOptionalFieldsProps {
    values: IProFormaValues | any;
    handleInputChange: (name: string, value: string | number) => void;
    floorAreaVaries?: boolean;
    setFloorAreaVaries?: (value: boolean) => void;
    storyAreas?: (number | null)[];
    handleStoryAreaChange?: (story: number, area: number | null) => void;
    rowClassName?: string;
    labelClassName?: string;
    inputClassName?: string;
    useSocketContext?: boolean;
}

const ProFormaSizeOptionalFields = ({
    values,
    handleInputChange,
    floorAreaVaries,
    setFloorAreaVaries,
    storyAreas,
    handleStoryAreaChange,
    rowClassName = FORM_ROW_CLASS_NAME,
    labelClassName = FORM_LABEL_CLASS_NAME,
    inputClassName = 'w-25',
    useSocketContext = false,
}: IProFormaSizeOptionalFieldsProps) => {
    let socketContext: any = null;
    let proForma: any = null;

    try {
        if (useSocketContext) {
            const context = useProFormaSocketContext();
            socketContext = context;
            proForma = context.proForma;
        }
    } catch (e) {
        console.debug('Socket context not available, using basic mode');
    }

    const getStoryValue = () => ('averageStories' in values ? values.averageStories : values.stories);

    const getStoryFieldName = () => ('averageStories' in values ? 'averageStories' : 'stories');

    const sfPerFloor = getStoryValue() && getStoryValue() > 0 && values.builtGrossSquareFootage
        ? numeralFormatter(values.builtGrossSquareFootage / getStoryValue(), '0,0') : '0';

    const isApartment = proForma && [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);

    return (
        <div className="mt-2">
            <hr className="mb-3" />
            <div className="mb-3">
                <div className="text-dark font-18">Optional fields</div>
                <div className="text-muted">These fields are informational - they do not impact calculations.</div>
            </div>
            <div className={rowClassName}>
                <NSLabel className={labelClassName}>Buildings</NSLabel>
                <NSInputNumberStepper
                    name="buildings"
                    value={values.buildings}
                    onChange={(count: number) => {
                        if (socketContext?.handleUpdateProForma) {
                            socketContext.handleUpdateProForma([{
                                key: 'buildings',
                                value: String(count),
                            }]);
                        }
                        handleInputChange('buildings', count);
                    }}
                    min={1}
                    inputGroupClassName={inputClassName}
                    step={1}
                />
            </div>
            <div className={rowClassName}>
                <NSLabel className={labelClassName}>
                    {values.buildings <= 1 ? 'Stories' : 'Avg. # of stories'}
                </NSLabel>
                <div className={floorAreaVaries && useSocketContext
                    ? 'd-flex flex-column w-100 p-2 NSCard--level-3'
                    : `d-flex flex-column ${inputClassName}`}
                >
                    <NSInputNumberStepper
                        name={getStoryFieldName()}
                        value={getStoryValue()}
                        onChange={(count: number) => {
                            if (socketContext?.handleUpdateProForma) {
                                socketContext.handleUpdateProForma([{
                                    key: getStoryFieldName(),
                                    value: String(count),
                                }]);
                            }
                            handleInputChange(getStoryFieldName(), count);
                        }}
                        min={1}
                        inputGroupClassName={floorAreaVaries && useSocketContext ? 'w-50' : ''}
                        step={1}
                    />
                    <RenderIf isTrue={useSocketContext
                        && !floorAreaVaries && values.buildings === 1
                        && Math.round(getStoryValue()) > 0}
                    >
                        <div className="text-muted mt-1">
                            SF per floor:
                            {' '}
                            <span className="text-dark">{sfPerFloor}</span>
                        </div>
                    </RenderIf>
                    <RenderIf isTrue={useSocketContext
                        && !isApartment && values.buildings === 1 && Math.round(getStoryValue()) > 1}
                    >
                        <div className="d-flex pt-1 pb-2">
                            <NSSwitch
                                name="floorAreaVaries"
                                id="1"
                                checked={floorAreaVaries || false}
                                containerClassName="pr-1"
                                onChange={() => {
                                    const newFloorAreaVaries = !floorAreaVaries;
                                    if (setFloorAreaVaries) {
                                        setFloorAreaVaries(newFloorAreaVaries);
                                    }
                                    if (socketContext?.handleUpdateProForma) {
                                        socketContext.handleUpdateProForma([{ key: 'isFloorPlateAreaVaried', value: newFloorAreaVaries }]);
                                    }
                                }}
                            />
                            <NSLabel className={labelClassName}>
                                Floor plate area varies
                            </NSLabel>
                        </div>
                        <StoryAreaBreakdown
                            storyAreas={storyAreas || []}
                            floorAreaVaries={floorAreaVaries || false}
                            averageStories={Math.round(getStoryValue())}
                            onAreaChange={handleStoryAreaChange || (() => { })}
                        />
                    </RenderIf>
                </div>
            </div>
        </div>
    );
};

export default ProFormaSizeOptionalFields;
